import React, { useState, useEffect } from 'react';
import ModalBody from '../../ModalBody';
import CustomTextInput from '../../../CustomTextInput';
import { useTranslation } from 'react-i18next';
import { FormType } from '../../../../services/apiSLTP/qhse/forms/formTypes';
import { crudStatus } from '../../../../utils/enums';
import { useCreateFormTypeMutation, useUpdateFormTypeMutation } from '../../../../hooks/mutations/useFormTypeMutation';
import ToggleLine from '../../../ToggleLine';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';

interface ModalProps {
    formType: FormType | null;
    isEditMode?: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    showEditModal: boolean;
    refetch: () => void;
}

const UpdateFormTypeModal: React.FC<ModalProps> = ({ formType, isEditMode = true, showEditModal, setShowEditModal, refetch }) => {
    const { t } = useTranslation();

    const [designation, setDesignation] = useState(formType?.designation || '');
    const [description, setDescription] = useState(formType?.description || '');
    const [status, setStatus] = useState(formType?.crudStatus || crudStatus.OK);
    const [color, setColor] = useState(formType?.color || 'blue');

    const { mutate: updateFormType } = useUpdateFormTypeMutation({
        onSuccess: () => {
            refetch();
            setShowEditModal(false);
        },
        onError: (error) => console.error('Error updating form type:', error.message),
    });

    const { mutate: createFormType } = useCreateFormTypeMutation({
        onSuccess: () => {
            refetch();
            setShowEditModal(false);
        },
        onError: (error) => console.error('Error creating form type:', error.message),
    });

    const isValidForm = designation.trim() !== '';

    const handleSubmit = () => {
        const payload = {
            designation: designation.trim(),
            description: description.trim(),
            crudStatus: status,
            color: color,
        };

        if (isEditMode && formType?._id) {
            updateFormType({ formTypeId: formType._id, data: payload });
        } else {
            createFormType(payload);
        }
    };

    return (
        <ModalBody
            title={t('formType')}
            subTitle={isEditMode ? t('update') : t('add')}
            closeText={t('cancel')}
            okText={t('validate')}
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            disabledOk={!isValidForm}
            handleOk={handleSubmit}
        >
            <CustomTextInput label={t('name')} value={designation} onChange={setDesignation} required />
            <CustomTextInput label={t('description')} value={description} onChange={setDescription} multiline />
            <ToggleLine
                checked={status === crudStatus.OK}
                onChange={(checked) => setStatus(checked ? crudStatus.OK : crudStatus.INACTIVE)}
                title={capitalizeFirstLetter(t('active'))}
            />
            <CustomTextInput label={capitalizeFirstLetter(t('color'))} type="color" value={color} onChange={setColor} />
        </ModalBody>
    );
};

export default UpdateFormTypeModal;
